import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Send, Clock } from 'lucide-react';
import { SearchSuggestion } from '../../types';
import Button from '../common/Button';
import { motion } from 'framer-motion';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  isDisabled?: boolean;
  suggestions?: SearchSuggestion[];
}

const ChatInput: React.FC<ChatInputProps> = ({ 
  onSendMessage,
  isDisabled = false,
  suggestions = [],
}) => {
  const { labels } = useLanguage();
  const [message, setMessage] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Focus the input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (message.trim() && !isDisabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };
  
  const handleUseSuggestion = (text: string) => {
    onSendMessage(text);
  };
  
  return (
    <div className="w-full max-w-4xl mx-auto">
      {suggestions && suggestions.length > 0 && (
        <div className="mb-4">
          <h3 className="text-sm font-semibold text-primary-700 mb-3">{labels.suggestedSearches}</h3>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion) => (
              <motion.div
                key={suggestion.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <button
                  onClick={() => handleUseSuggestion(suggestion.text)}
                  className="px-4 py-2 bg-white border-2 border-primary-200 rounded-lg text-sm font-medium text-primary-700 hover:bg-primary-50 hover:border-primary-300 transition-all duration-200 shadow-sm"
                  disabled={isDisabled}
                >
                  {suggestion.text}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center">
          <textarea
            ref={inputRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={labels.typeMessageHere}
            className="w-full pl-4 pr-20 py-3 bg-white border-2 border-primary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-300 resize-none min-h-[56px] max-h-32 shadow-sm"
            rows={1}
            disabled={isDisabled}
          />
          <div className="absolute right-2 flex items-center space-x-1">
            <Button
              type="submit"
              variant="primary"
              size="sm"
              className="rounded-lg h-10 w-10 p-0 flex items-center justify-center"
              disabled={!message.trim() || isDisabled}
            >
              <Send size={18} />
            </Button>
          </div>
        </div>
        {isDisabled && (
          <div className="absolute bottom-full mb-2 left-0 right-0 bg-primary-900 text-white py-1 px-2 rounded text-xs">
            <div className="flex items-center">
              <Clock size={12} className="mr-1" />
              {labels.processing}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ChatInput;